import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import propTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Grid from '@material-ui/core/Grid';
import Checkbox from '@material-ui/core/Checkbox';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import PageLoader from 'components/PageLoader';
import { Option, isIOS } from 'components/Option';
import photoPlaceholder from 'assets/images/user-photo-placeholder.svg';
import { SCHEDULE_VIEW_TYPES } from 'pages/ScheduleV6/constants/constants';
import {
  PUBLISH_SHIFT,
  PUBLISH_SHIFT_LABELS,
  PUBLISH_SHIFT_ORDER,
} from 'pages/ScheduleV6/constants/shifts';
import { updateSheets } from 'store/schedule/schedulePageOperation';
import classnames from 'classnames';
import styles from './styles';


const PublishUpdateModal = ({ classes, isOpen, onClose, data, onSubmit, openSnackbar }) => {
  const dispatch = useDispatch()
  const { scheduleView } = useSelector((store) => store.schedule);

  const [isLoading, setIsLoading] = useState(false);
  const [selectedWorkersIds, setSelectedWorkers] = useState([]);

  const showShiftSelector = data.type === 'update' && data.availableShifts?.size > 1;
  const shiftOptions = useMemo(
    () => PUBLISH_SHIFT_ORDER.filter((option) => {
      if (option === PUBLISH_SHIFT.FULL) return true;
      return data.availableShifts?.has?.(option);
    }),
    [data.availableShifts]
  );

  const workers = useMemo(
    () => data.workers.map((w) => ({ ...w, text: `Scheduled for ${w.addedTo.join(', ')}.` })),
    [data.workers]
  );

  useEffect(() => {
    setSelectedWorkers(data.workers.map((w) => w._id));
  }, [data.workers]);

  const updateWorker = (id) => () => {
    if (selectedWorkersIds.includes(id)) {
      return setSelectedWorkers(selectedWorkersIds.filter((workerId) => workerId !== id));
    }
    setSelectedWorkers([...selectedWorkersIds, id]);
  };

  const handleUpdateAllSelectedFlag = () => {
    if (selectedWorkersIds.length) {
      setSelectedWorkers([]);
    } else {
      setSelectedWorkers(workers.map((w) => w._id));
    }
  };

  const handleShiftChange = (event) => {
    data.onShiftChange?.(event.target.value);
  };

  const handleSubmit = async () => {
    const workersIds = workers
      .filter((worker) => selectedWorkersIds.includes(worker._id))
      .map((worker) => worker._id);

    setIsLoading(true);
    try {
      const res = await onSubmit(workersIds);
      if (scheduleView === SCHEDULE_VIEW_TYPES.daily) {
        dispatch(updateSheets())
      }
      onClose();

      if (res?.errorMessage) {
        openSnackbar('error', res.errorMessage);
      }
    } catch (e) {
      openSnackbar('error', e.response?.data?.message || e?.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={onClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          classes: {
            root: classes.dialogRoot,
          },
        }}
      >
        <div className={classes.header}>
          <Typography variant="h5" color="textPrimary">
            {data.type === 'publish' ? 'Publish' : 'Update'} Schedule
          </Typography>
          <IconButton
            className={classes.closeButton}
            onClick={onClose}
            aria-label="Close"
            disableRipple
            disabled={isLoading}
          >
            <CloseIcon />
          </IconButton>
        </div>
        <PageLoader loading={isLoading} small>
          <>
            <DialogContent className={classes.paddingBox}>
              {showShiftSelector ? (
                <TextField
                  select
                  id="updateShiftSelection"
                  name="updateShiftSelection"
                  value={data.selectedShift || PUBLISH_SHIFT.FULL}
                  onChange={handleShiftChange}
                  className={classes.shiftSelector}
                  variant="outlined"
                  SelectProps={{ native: isIOS }}
                >
                  {shiftOptions.map((option) => (
                    <Option key={option} value={option}>
                      {PUBLISH_SHIFT_LABELS[option]}
                    </Option>
                  ))}
                </TextField>
              ) : null}
              <Typography variant="h6" color="textPrimary" className={classes.marginBottom}>
                {workers.length ? 'Notices will be sent to the following employees:' : ''}
              </Typography>

              {workers.map((w) => (
                <Grid container key={w._id} spacing={2}>
                  <Grid item xs={6}>
                    <div className={classes.inlineFlex}>
                      <Checkbox
                        checked={selectedWorkersIds.includes(w._id)}
                        onChange={updateWorker(w._id)}
                        color="primary"
                        className={classes.checkbox}
                      />
                      <div
                        className={classnames(
                          classes.photoWrapper,
                          classes[`${w.user?.profile?.shifts?.timeOfDay?.toLowerCase()}Border`]
                        )}
                        onClick={updateWorker(w._id)}
                      >
                        <img src={photoPlaceholder} className={classes.photo} />
                      </div>
                      <Typography
                        variant="body1"
                        color="textPrimary"
                        className={classnames(
                          classes.name,
                          classes[w.user?.profile?.shifts?.timeOfDay?.toLowerCase()]
                        )}
                        onClick={updateWorker(w._id)}
                      >
                        {w.user?.username}
                      </Typography>
                    </div>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body1" color="textPrimary">
                      {w.text}
                    </Typography>
                  </Grid>
                </Grid>
              ))}
            </DialogContent>
            {workers.length ? (
              <div className={classes.flexBoxClearBtn}>
                <Button
                  color="secondary"
                  variant="text"
                  className={classnames(classes.extraBtn, classes.selectBtn)}
                  onClick={handleUpdateAllSelectedFlag}
                  disableTouchRipple
                >
                  {selectedWorkersIds.length ? 'Clear' : 'Select'} all
                </Button>
              </div>
            ) : null}
            <DialogActions>
              <div className={classes.flexBox}>
                <Button
                  color="primary"
                  variant="outlined"
                  className={classes.saveButton}
                  onClick={handleSubmit}
                  disableTouchRipple
                  disabled={isLoading}
                >
                  Send Updates
                </Button>
              </div>
            </DialogActions>
          </>
        </PageLoader>
      </Dialog>
    </>
  );
};

PublishUpdateModal.propTypes = {
  classes: propTypes.object.isRequired,
  isOpen: propTypes.bool.isRequired,
  onClose: propTypes.func.isRequired,
  onSubmit: propTypes.func.isRequired,
  data: propTypes.object.isRequired,
  openSnackbar: propTypes.func.isRequired,
};

export default withStyles(styles)(PublishUpdateModal);
